import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { StateSelection } from "@components/common";
import { JurisdictionService } from "@services";
import utils from "@utils";

import "./JurisdictionForm.scss";

class JurisdictionForm extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			jurisdictionName: "",
			jurisdictionDepartment: "",
			jurisdictionAddress: "",
			jurisdictionCity: "",
			jurisdictionCounty: "",
			jurisdictionState: "",
			jurisdictionZipCode: "",
			loading: false,
			errors: {
				jurisdictionName: false,
				jurisdictionDepartment: false,
				jurisdictionAddress: false,
				jurisdictionCity: false,
				jurisdictionCounty: false,
				jurisdictionState: false,
				jurisdictionZipCode: false,
			},
		};
	}

	componentDidMount() {
		const {
			router: {
				params: { jurisdictionId },
			},
		} = this.props;
		if (jurisdictionId !== "add") this.getJurisdictionById(jurisdictionId);
	}

	// Service Methods
	/**
	 * Retrieves jurisdiction details using the provided Id
	 * @param {Number|String} jurisdictionId The ID number for the jurisdiction
	 */
	getJurisdictionById = jurisdictionId => {
		const { lang } = global;
		const { router } = this.props;
		this.setState({ loading: true });
		JurisdictionService.getById(jurisdictionId)
			.then(({ data }) => {
				this.setState({
					jurisdictionName: data.Name || "",
					jurisdictionDepartment: data.DepartmentType || "",
					jurisdictionAddress: data.StreetAddress1 || "",
					jurisdictionCity: data.City || "",
					jurisdictionCounty: data.County || "",
					jurisdictionState: data.StateId || "",
					jurisdictionZipCode: data.Zipcode || "",
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Jurisdiction Details"),
					title: "Error",
				});
				router.push("/jurisdictions");
			})
			.finally(() => {
				this.setState({ loading: false });
			});
	};

	/**
	 * Creates a new jurisdiction using details from the `params` parameter.
	 * * Upon a successful update, the user is redirected to the jurisdictions list
	 */
	createJurisdiction = params => {
		const { lang } = global;
		const { router } = this.props;
		JurisdictionService.create(params)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Jurisdiction successfully created"),
				});
				router.push("/jurisdictions");
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error creating Jurisdiction"),
					title: "Error",
				});
			});
	};

	/**
	 * Makes updates to an existing jurisdiction using details from the `params` parameter.
	 * Upon a successful update, the user is redirected to the jurisdictions list
	 */
	editJurisdiction = params => {
		const { lang } = global;
		const { router } = this.props;
		JurisdictionService.edit(params)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Jurisdiction successfully updated"),
				});
				router.push("/jurisdictions");
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error updating the jurisdiction"),
					title: "Error",
				});
			});
	};
	// Service Methods

	handleInputChange = e => {
		const {
			target: { name },
		} = e;
		let {
			target: { value },
		} = e;
		if (name === "jurisdictionZipCode") {
			value = value.replace(/\D/g, "");
		}
		this.setState({ [name]: value });
	};

	/**
	 * Validates that all form fields have appropriate values
	 * @returns {Object} an object with the `isValid` property
	 * @todo Update this method so it returns a boolean value instead of an object
	 */
	validateForm = () => {
		const {
			jurisdictionName,
			jurisdictionDepartment,
			jurisdictionAddress,
			jurisdictionCity,
			jurisdictionCounty,
			jurisdictionState,
			jurisdictionZipCode,
			errors,
		} = this.state;

		errors.jurisdictionName = jurisdictionName.toString().trim() === "";
		errors.jurisdictionDepartment = jurisdictionDepartment.toString().trim() === "";
		errors.jurisdictionAddress = jurisdictionAddress.toString().trim() === "";
		errors.jurisdictionCity = jurisdictionCity.toString().trim() === "";
		errors.jurisdictionCounty = jurisdictionCounty.toString().trim() === "";
		errors.jurisdictionState = jurisdictionState.toString() === "";
		errors.jurisdictionZipCode = jurisdictionZipCode.toString().trim().length < 5;

		this.setState({ errors });

		const isValid = Object.values(errors).filter(error => error === true).length === 0;

		return { isValid };
	};

	/**
	 * Builds an object that is then passed to {@link createJurisdiction} (or {@link editJurisdiction} if editing a jurisdiction)
	 * @param {Event} e The HTML event handled by this method
	 */
	handleOnSave = e => {
		e.preventDefault();
		const {
			router: {
				params: { jurisdictionId },
			},
		} = this.props;
		const {
			jurisdictionName,
			jurisdictionDepartment,
			jurisdictionAddress,
			jurisdictionCity,
			jurisdictionCounty,
			jurisdictionState,
			jurisdictionZipCode,
		} = this.state;
		const dataParams = {
			Name: jurisdictionName,
			DepartmentType: jurisdictionDepartment,
			StreetAddress1: jurisdictionAddress,
			City: jurisdictionCity,
			County: jurisdictionCounty,
			StateId: jurisdictionState,
			Zipcode: jurisdictionZipCode,
		};

		const validation = this.validateForm();
		if (!validation.isValid) return;

		if (jurisdictionId !== "add") {
			this.editJurisdiction({ ...dataParams, Id: jurisdictionId });
		} else {
			this.createJurisdiction(dataParams);
		}
	};

	render() {
		const { lang } = global;
		const {
			router,
			router: {
				params: { jurisdictionId },
			},
		} = this.props;
		const {
			jurisdictionName,
			jurisdictionDepartment,
			jurisdictionAddress,
			jurisdictionCity,
			jurisdictionCounty,
			jurisdictionState,
			jurisdictionZipCode,
			loading,
			errors,
		} = this.state;

		return (
			<div className="jurisdiction-form container">
				{/* Jurisdiction Header */}
				<div className="row">
					<div className="col-xs-12">
						<Choose>
							<When condition={jurisdictionId !== "add"}>
								<h1>{lang.t("Edit Jurisdiction")}</h1>
							</When>
							<Otherwise>
								<h1>{lang.t("Add Jurisdiction")}</h1>
							</Otherwise>
						</Choose>
					</div>
				</div>
				{/* Jurisdiction Header */}

				{/* Jurisdiction Form */}
				<div className="row">
					<div className="col-md-8 col-md-offset-2 col-sm-12">
						<Choose>
							<When condition={loading}>
								<div className="text-center">
									<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
								</div>
							</When>
							<Otherwise>
								<div className="row form-group">
									<label
										htmlFor="jurisdictionName"
										className="col-sm-3"
									>{`${lang.t("Name")}:`}</label>
									<div className="col-xs-12 col-sm-9">
										<input
											type="text"
											id="jurisdictionName"
											name="jurisdictionName"
											className={classNames("form-control", {
												"has-error": errors.jurisdictionName,
											})}
											value={jurisdictionName}
											onChange={e => this.handleInputChange(e)}
										/>
									</div>
								</div>
								<div className="row form-group">
									<label
										htmlFor="jurisdictionDepartment"
										className="col-sm-3"
									>{`${lang.t("Department")}:`}</label>
									<div className="col-xs-12 col-sm-9">
										<input
											type="text"
											id="jurisdictionDepartment"
											name="jurisdictionDepartment"
											className={classNames("form-control", {
												"has-error": errors.jurisdictionDepartment,
											})}
											value={jurisdictionDepartment}
											onChange={e => this.handleInputChange(e)}
										/>
									</div>
								</div>
								<div className="row form-group">
									<label
										htmlFor="jurisdictionAddress"
										className="col-sm-3"
									>{`${lang.t("Address")}:`}</label>
									<div className="col-xs-12 col-sm-9">
										<input
											type="text"
											id="jurisdictionAddress"
											name="jurisdictionAddress"
											className={classNames("form-control", {
												"has-error": errors.jurisdictionAddress,
											})}
											value={jurisdictionAddress}
											onChange={e => this.handleInputChange(e)}
										/>
									</div>
								</div>
								<div className="row form-group">
									<label
										htmlFor="jurisdictionCity"
										className="col-sm-3"
									>{`${lang.t("City")}:`}</label>
									<div className="col-xs-12 col-sm-9">
										<input
											type="text"
											id="jurisdictionCity"
											name="jurisdictionCity"
											onChange={e => this.handleInputChange(e)}
											className={classNames("form-control", {
												"has-error": errors.jurisdictionCity,
											})}
											value={jurisdictionCity}
										/>
									</div>
								</div>
								<div className="row form-group">
									<label
										htmlFor="jurisdictionCounty"
										className="col-sm-3"
									>{`${lang.t("County")}:`}</label>
									<div className="col-xs-12 col-sm-9">
										<input
											type="text"
											id="jurisdictionCounty"
											name="jurisdictionCounty"
											className={classNames("form-control", {
												"has-error": errors.jurisdictionCounty,
											})}
											value={jurisdictionCounty}
											onChange={e => this.handleInputChange(e)}
										/>
									</div>
								</div>
								<div className="row form-group">
									<label
										htmlFor="jurisdictionState"
										className="col-sm-3"
									>{`${lang.t("State")}:`}</label>
									<div className="col-xs-12 col-sm-9">
										<StateSelection
											id="jurisdictionState"
											name="jurisdictionState"
											className={classNames("form-control", {
												"has-error": errors.jurisdictionState,
											})}
											value={jurisdictionState}
											onChange={e => this.handleInputChange(e)}
										/>
									</div>
								</div>
								<div className="row form-group">
									<label
										htmlFor="jurisdictionZipCode"
										className="col-sm-3"
									>{`${lang.t("Zip Code")}:`}</label>
									<div className="col-xs-12 col-sm-9">
										<input
											type="tel"
											id="jurisdictionZipCode"
											name="jurisdictionZipCode"
											className={classNames("form-control", {
												"has-error": errors.jurisdictionZipCode,
											})}
											value={jurisdictionZipCode}
											onChange={e => this.handleInputChange(e)}
											maxLength="5"
										/>
									</div>
								</div>

								<div className="row form-group">
									<div className="col-sm-9 col-sm-offset-3 text-center">
										<button
											type="button"
											className="btn btn-link"
											onClick={() => router.push("/jurisdictions")}
										>
											{lang.t("Cancel")}
										</button>
										<button
											type="button"
											className="btn btn-primary"
											onClick={e => this.handleOnSave(e)}
										>
											{lang.t("Save")}
										</button>
									</div>
								</div>
							</Otherwise>
						</Choose>
					</div>
				</div>
				{/* Jurisdiction Form */}
			</div>
		);
	}
}

JurisdictionForm.propTypes = {
	router: PropTypes.object.isRequired,
};

// JurisdictionForm.defaultProps = {
// };

export default JurisdictionForm;
